import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import PopoverAlignmentEnum from '../../enums/PopoverAlignmentEnum.js';
import type PopoverDirectionEnum from '../../enums/PopoverDirectionEnum.js';
import PopoverPositioner from '../popover-positioner/PopoverPositioner.js';

/**
 * Popover class.
 *
 * @event animationEnd When animation end.
 *
 * @slot Element to trigger popover.
 */
export default class Popover extends KompisElement {
	// Alignment of popover.
	@property({ type: String })
	public alignment: PopoverAlignmentEnum = PopoverAlignmentEnum.center;

	// Direction of popover.
	@property({ type: String })
	public direction: PopoverDirectionEnum | null = null;

	// Z index.
	@property({ type: Number })
	public zIndex = 3000;

	// Visible.
	@property({ type: Boolean })
	public visible = false;

	// Enabled wiggle.
	@property({ type: Boolean })
	public wiggle = false;

	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		const { color, border } = this.theme;
		return html`
			<style>
				kompis-popover-positioner {
					position: absolute;
					left: 0;
					right: 0;
					bottom: 0;
					top: 0;
				}
				.content {
					color: ${color.neutral.grey900};
					background-color: ${color.neutral.white};
					border-radius: ${border.radius.radius8 / 16}rem;
					border-width: 0;
					box-shadow: 0 ${4 / 16}rem ${16 / 16}rem rgba(0, 0, 0, 0.1);
				}
			</style>
			${this.getPopoverPositioner()}
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-popover-positioner', PopoverPositioner);

		super.connectedCallback();
	}
	/**
	 * The intersection between iframe and visible area.
	 *
	 * @returns - Html.
	 */
	private getPopoverPositioner(): RenderResult {
		if (!this.visible) {
			return null;
		}

		return html`
			<kompis-popover-positioner
				@animationEnd="${() =>
					this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }))}"
				.visible="${this.visible}"
				.theme="${this.theme}"
				.wiggle="${this.wiggle}"
				.zIndex="${this.zIndex}"
				.alignment="${this.alignment}"
				.direction="${this.direction}"
			>
				<div class="content">
					<slot></slot>
				</div>
			</kompis-popover-positioner>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-popover': Popover;
	}
}
